import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2 } from "lucide-react";
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, LabelList } from "recharts";
import { tooltipStyle } from "./oversiktConstants";
import { formatNumber, dec, num } from "@/lib/formatters";

const kvm = (v) => (v == null ? "—" : `${formatNumber(Math.round(v))} m²`);
const kr = (v) => (v == null ? "—" : `${formatNumber(Math.round(v))} kr`);
const mill = (v) => (v == null ? "—" : `${formatNumber(Math.round(v / 1000))} mill. kr`);

const MAAL = [
  { key: "areal_per_innb", label: "Areal på formålsbygg per innbyggjar", format: (v) => `${dec(v, 2)} m²`, testId: "areal", desimalar: 2 },
  { key: "vedlikehald_per_kvm", label: "Vedlikehald per kvadratmeter", format: (v) => `${formatNumber(v)} kr`, testId: "vedlikehald", norm: true },
  { key: "etterslep_per_innb", label: "Anslått etterslep per innbyggjar (høgt anslag)", format: (v) => `${formatNumber(v)} kr`, testId: "etterslep" },
];

const EiendomCompareChart = ({ data, colors }) => {
  const rows = Object.values(data?.kommuner || {});
  if (rows.length === 0) return null;
  const land = data.land;
  const verdi = (r, key) => (key === "etterslep_per_innb" ? r.etterslep?.hoeg?.per_innb : r.latest?.[key]);

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2" data-testid="compare-eiendom-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#103766]" />
          Eigedomsmasse og vedlikehald {rows[0].year}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">
          Kvadratmeter formålsbygg per innbyggjar, kva kommunane brukar på vedlikehald per kvadratmeter og kor stort etterslepet kan vere. Stipla
          linje er landssnittet.
        </p>
      </CardHeader>
      <CardContent className="pt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {MAAL.map((m) => {
          const bars = rows.filter((r) => verdi(r, m.key) != null).map((r) => ({ name: r.name, verdi: verdi(r, m.key) }));
          if (bars.length === 0) return null;
          const norm = m.norm ? rows[0].latest?.norm_lav : null;
          return (
            <div key={m.key}>
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">{m.label}</p>
              <div className="h-[240px]" data-testid={`compare-eiendom-${m.testId}`}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bars} barCategoryGap="35%" margin={{ top: 18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                    <XAxis dataKey="name" stroke="#8AA4BF" fontSize={12} />
                    <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => num(v, m.desimalar || 0)} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [m.format(v), m.label]} />
                    {land?.[m.key] != null && m.key !== "etterslep_per_innb" && (
                      <ReferenceLine y={land[m.key]} stroke="#4B647F" strokeDasharray="4 4"
                        label={{ value: `Landet ${m.format(land[m.key])}`, position: "insideTopRight", fontSize: 11, fill: "#4B647F" }} />
                    )}
                    {norm != null && (
                      <ReferenceLine y={norm} stroke="#C0392B" strokeDasharray="5 4"
                        label={{ value: `Normkrav ${formatNumber(norm)} kr`, position: "insideBottomLeft", fontSize: 11, fill: "#C0392B" }} />
                    )}
                    <Bar dataKey="verdi" radius={[4, 4, 0, 0]}>
                      {bars.map((b, i) => <Cell key={b.name} fill={colors[i % colors.length]} />)}
                      <LabelList dataKey="verdi" position="top" formatter={(v) => num(v, m.desimalar || 0)} fontSize={12} fill="#1F2A36" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
        <div className="lg:col-span-3 overflow-x-auto">
          <table className="w-full text-xs" data-testid="compare-eiendom-table">
            <thead>
              <tr className="border-b border-[#E6E9F0] text-[#4B647F]">
                <th className="py-2 pr-3 text-left font-medium">Kommune</th>
                <th className="py-2 px-3 text-right font-medium">Eigd areal</th>
                <th className="py-2 px-3 text-right font-medium">Leigd areal</th>
                <th className="py-2 px-3 text-right font-medium">Per innbyggjar</th>
                <th className="py-2 px-3 text-right font-medium">Vedlikehald per m²</th>
                <th className="py-2 px-3 text-right font-medium">Manglar per m² i året</th>
                <th className="py-2 px-3 text-right font-medium">Energi per m²</th>
                <th className="py-2 pl-3 text-right font-medium">Anslått etterslep</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.name} className="border-b border-[#F1F4F8] text-[#1F2A36]" data-testid={`compare-eiendom-row-${i}`}>
                  <td className="py-2 pr-3 font-medium" style={{ color: colors[i % colors.length] }}>{r.name}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{kvm(r.latest.areal_eid)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{kvm(r.latest.areal_leid)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{dec(r.latest.areal_per_innb, 2)} m²</td>
                  <td className="py-2 px-3 text-right tabular-nums">{kr(r.latest.vedlikehald_per_kvm)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {r.latest.gap_lav_per_kvm == null
                      ? "—"
                      : `${formatNumber(Math.max(0, r.latest.gap_lav_per_kvm))}–${formatNumber(Math.max(0, r.latest.gap_hoeg_per_kvm))} kr`}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">{kr(r.latest.energi_per_kvm)}</td>
                  <td className="py-2 pl-3 text-right tabular-nums">
                    {r.etterslep
                      ? `${formatNumber(Math.round(r.etterslep.lav.total_kr / 1000))}–${formatNumber(Math.round(r.etterslep.hoeg.total_kr / 1000))} mill. kr (${r.etterslep.aar_fra}–${r.etterslep.aar_til})`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-[#8AA4BF] mt-2">
            Normkravet for verdibevarande vedlikehald er 100 og 170 kr per m² i 2009-kroner (KS/Multiconsult), prisjustert med SSB sin
            byggjekostnadsindeks (08655). Etterslepet er summen av (norm − faktisk vedlikehald) × areal for åra kommunen har tal for – difor står
            årsintervallet i parentes, og tal for kommunar med ulike periodar kan ikkje samanliknast direkte. Kjelder: SSB 11906, 12051 og 12905.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default EiendomCompareChart;
